"use client";

import React from "react";
import { FolderKanban, ListChecks, ShieldAlert, MapPin, Camera, MessageSquareHeart, Network, Map, ArrowRight, Info } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const topics = [{
  icon: FolderKanban,
  title: "Business categories",
  description: "I select and organise your primary and secondary categories to reflect what you actually offer without muddying your core local relevance."
}, {
  icon: ListChecks,
  title: "Listed services",
  description: "I structure your full service menu with clear descriptions, helping search engines match your profile to specific customer enquiries."
}, {
  icon: ShieldAlert,
  title: "Business info accuracy",
  description: "I audit your name, address, phone numbers, opening hours, and business attributes so customers and search crawlers never see conflicting details."
}, {
  icon: MapPin,
  title: "Service areas",
  description: "I define the exact towns, postcodes, and regional boundaries you serve across Bedfordshire, ensuring clear regional coverage."
}, {
  icon: Camera,
  title: "High-quality photos",
  description: "I provide actionable guidance for uploading authentic, geotagged workspace, team, and project imagery that builds real visitor trust."
}, {
  icon: MessageSquareHeart,
  title: "Review management",
  description: "I create straightforward review collection workflows and help you craft professional, keyword-conscious responses to all feedback."
}, {
  icon: Network,
  title: "Local relevance signals",
  description: "I align your profile with local citations, directories, and website landing pages to reinforce your local entity prominence."
}, {
  icon: Map,
  title: "Google Maps visibility",
  description: "I maintain regular updates, posts, and product feeds to signal active business operations and support Map pack discovery."
}];
export default function ProfileOptimisation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="googlebusinessprofileoptimisation" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header Block */}
        <div className="flex flex-col gap-6 max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-medium px-3 py-1 rounded-full text-xs tracking-wide">
              Local Search Focus
            </Badge>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default">
            Google Business Profile optimisation in Bedford
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            I work directly with you to audit, configure, and maintain your
            Google Business Profile. You get hands-on attention from an
            independent SEO consultant focused on helping nearby customers find
            and trust your business.
          </p>

          {/* Honest Disclaimer Callout */}
          <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground shadow-xs">
            <Info className="size-5 text-primary shrink-0 mt-0.5" />
            <p>
              <strong className="text-foreground font-semibold">
                Transparent ranking notice:
              </strong>{" "}
              Search engines constantly update their local algorithms. I do not
              promise guaranteed number-one rankings or unrealistic positions,
              focusing instead on sustainable, accurate, and high-standard
              optimisation.
            </p>
          </div>
        </div>

        {/* 8-Topic Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {topics.map((topic, i) => {
          const IconComponent = topic.icon;
          return <motion.div key={topic.title} variants={itemVariants} data-index={i} className="h-full">
                <Card className="h-full bg-card border border-border rounded-xl shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/40 flex flex-col justify-between">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center mb-4 text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <CardTitle className="text-lg font-semibold text-card-foreground">
                        {topic.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {topic.description}
                      </p>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom CTA Block */}
        <div className="mt-16 md:mt-20 pt-10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-center sm:text-left">
            <h3 className="text-xl font-semibold text-foreground">
              Ready to refine your local search presence?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Speak directly with me about your profile, local competition, and
              search goals.
            </p>
          </div>

          <Button asChild size="lg" className="rounded-md font-medium shrink-0">
            <Link to="/ContactPage" className="inline-flex items-center gap-2">
              <span>Book a Free SEO Consultation</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}